/**
 * Single source of truth for non-component route metadata.
 *
 * Where to plug in a new page:
 *   1. Add an entry to `ROUTES` below (with i18n labelKey + flags).
 *   2. Add the matching dictionary key in `app/src/i18n/dictionary.ts`.
 *   3. Register the JSX `<Route>` in `app/src/App.tsx` (component imports
 *      cannot be serialised here, so the route element stays in App).
 *   4. That's it — Topbar nav, mobile drawer, sitemap.xml and the build-
 *      time pre-render pick the route up automatically.
 *
 * This file is intentionally Vite-magic-free (no import.meta.glob, no
 * JSX) so that `scripts/build-seo.ts` (Node, run via tsx) can import it
 * straight from disk.
 */

export type NavKey =
  | "navHome"
  | "navMap"
  | "navBlog"
  | "navImpressum";

export interface RouteMeta {
  /** Exact URL path. For dynamic routes use the pattern (`/spot/:id`) but
   *  set `inSitemap: false` and resolve dynamic URLs separately. */
  path: string;
  /** i18n key for the human-readable label, used in nav. */
  labelKey?: NavKey;
  /** Show this route in the global Topbar nav (desktop + burger). */
  inNav?: boolean;
  /** Render to a standalone `web/<path>/index.html` at build time so
   *  social-media crawlers see real meta tags. The map route stays SPA
   *  because it's a live tool, not content. */
  prerender?: boolean;
  /** Emit a `<url>` entry in `web/sitemap.xml`. */
  inSitemap?: boolean;
  /** Used by Topbar to suppress the floating "Karte" CTA on the routes
   *  where it would be redundant (the map itself and spot pages). */
  hideMapCta?: boolean;
}

export const ROUTES: RouteMeta[] = [
  {
    path: "/",
    labelKey: "navHome",
    inNav: true,
    prerender: true,
    inSitemap: true,
  },
  {
    path: "/map",
    labelKey: "navMap",
    inNav: true,
    inSitemap: true,
    hideMapCta: true,
  },
  {
    path: "/spot/:spotId",
    inSitemap: false,
    hideMapCta: true,
  },
  {
    path: "/blog",
    labelKey: "navBlog",
    inNav: true,
    prerender: true,
    inSitemap: true,
  },
  {
    path: "/blog/:slug",
    // /blog/<slug> URLs are derived from markdown files; the build-seo
    // script enumerates them dynamically.
  },
  {
    path: "/impressum",
    labelKey: "navImpressum",
    inNav: true,
    prerender: true,
    inSitemap: true,
  },
];

/** Routes shown in the global navigation (Topbar + burger drawer). */
export const NAV_ROUTES = ROUTES.filter((r) => r.inNav);

/** Static routes pre-rendered at build time to `web/<path>/index.html`. */
export const PRERENDER_STATIC_ROUTES = ROUTES.filter((r) => r.prerender).map(
  (r) => r.path,
);

/** Static routes listed in sitemap.xml (dynamic ones — spots, blog
 *  posts — are appended by the build script). */
export const SITEMAP_STATIC_ROUTES = ROUTES.filter((r) => r.inSitemap).map(
  (r) => r.path,
);

/** True iff the current pathname should hide the "Karte" CTA. */
export function shouldHideMapCta(pathname: string): boolean {
  return ROUTES.some((r) => {
    if (!r.hideMapCta) return false;
    if (r.path.includes(":")) {
      // Trivial prefix match for parameterised routes (we only have
      // `/spot/:id` today). Good enough; expand if needed.
      const prefix = r.path.split(":")[0]; // "/spot/"
      return pathname.startsWith(prefix);
    }
    return pathname === r.path;
  });
}
